// src/admin/pages/DataroomFileSettingsPage.js
import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  FileX, 
  FileCheck,
  Info,
  Upload,
  Download,
  Eye,
  Printer,
  Save
} from 'lucide-react';
import { supabase } from '../../config';
import { getFileSecurityRules } from '../../products/dataroom/utils/fileSecurityRules';

const DataroomFileSettingsPage = () => {
  const rules = getFileSecurityRules();
  const [maxFileSize, setMaxFileSize] = useState(100); // MB
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data: settingsData, error } = await supabase
        .from('platform_settings')
        .select('*')
        .single();

      if (!error && settingsData?.settings) {
        setMaxFileSize(settingsData.settings.dataroom_max_file_size || 100);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: existingData } = await supabase
        .from('platform_settings')
        .select('*')
        .single();

      const currentSettings = existingData?.settings || {};
      const updatedSettings = {
        ...currentSettings,
        dataroom_max_file_size: maxFileSize
      };

      const { error } = await supabase
        .from('platform_settings')
        .upsert({
          id: 1,
          settings: updatedSettings,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
      alert('File size setting saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dataroom File Settings</h1>
          <p className="text-gray-600 mt-2">Configure file upload rules and security settings for DealRooms</p>
        </div>
      </div>

      {/* File Security Rules */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Shield className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">File Security Rules</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">Rules that protect the system from malicious files</p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Blocked File Types */}
          <div>
            <div className="flex items-center mb-3">
              <FileX className="h-5 w-5 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Blocked File Types</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              These file types are completely blocked from upload for security reasons:
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {rules.blockedExtensions.map((ext) => (
                  <div
                    key={ext}
                    className="bg-white px-3 py-2 rounded border border-red-200 text-sm font-mono text-red-700"
                  >
                    .{ext}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-red-100 rounded border border-red-300">
                <p className="text-sm text-red-800">
                  <strong>Why blocked:</strong> These file types include executables, scripts, and system files that could contain malware or be used to compromise system security.
                </p>
              </div>
            </div>
          </div>

          {/* Caution File Types */}
          <div>
            <div className="flex items-center mb-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Caution File Types</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              These file types are allowed but require additional scanning:
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex flex-wrap gap-2">
                {rules.cautionExtensions.map((ext) => (
                  <div
                    key={ext}
                    className="bg-white px-3 py-2 rounded border border-yellow-300 text-sm font-mono text-yellow-800"
                  >
                    .{ext}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-yellow-100 rounded border border-yellow-300">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Archive files (ZIP, RAR) are scanned for blocked content before extraction. SVG files may be converted to PNG to prevent script execution.
                </p>
              </div>
            </div>
          </div>

          {/* Allowed File Types */}
          <div>
            <div className="flex items-center mb-3">
              <FileCheck className="h-5 w-5 text-green-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Allowed File Types</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              These file types are safe and allowed for upload:
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {rules.allowedExtensions.map((ext) => (
                  <div
                    key={ext}
                    className="bg-white px-3 py-2 rounded border border-green-200 text-sm font-mono text-green-700"
                  >
                    .{ext}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File Size Limits */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Upload className="h-6 w-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">File Size Limits</h2>
            </div>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum File Size (MB)
              </label>
              <input
                type="number"
                value={maxFileSize}
                onChange={(e) => setMaxFileSize(parseInt(e.target.value) || 100)}
                disabled={loading}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="100"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum file upload size in megabytes</p>
            </div>
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div>
                <p className="font-medium text-gray-900">Current Setting</p>
                <p className="text-sm text-gray-600">Per file upload limit</p>
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {maxFileSize} MB
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Permission Types */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Shield className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">File Permission Types</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">Available permission levels for file access</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {/* View Only */}
            <div className="flex items-start p-4 border border-gray-200 rounded-lg">
              <div className="flex-shrink-0 mr-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">View Only</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Users can view files in the browser/preview but cannot download or print.
                  All files are watermarked with user information and timestamp.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">PDF: Watermarked</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Images: Display only</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Docs: Converted to PDF</span>
                </div>
              </div>
            </div>

            {/* View + Print */}
            <div className="flex items-start p-4 border border-gray-200 rounded-lg">
              <div className="flex-shrink-0 mr-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Printer className="h-5 w-5 text-purple-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">View + Print</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Users can view files and print them using the browser's print function.
                  Download button is disabled. All printed documents include watermarks.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">Print enabled</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">Download disabled</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">Watermarked</span>
                </div>
              </div>
            </div>

            {/* View + Download */}
            <div className="flex items-start p-4 border border-gray-200 rounded-lg">
              <div className="flex-shrink-0 mr-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Download className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">View + Download</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Full access - users can view, print, and download files.
                  Downloaded files are permanently watermarked with user information.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Full access</span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Download enabled</span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Watermarked files</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How Permissions Apply by File Type */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Info className="h-6 w-6 text-indigo-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">How Permissions Apply by File Type</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    View Only
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    View + Print
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    View + Download
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">PDF</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Watermarked viewer only</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Viewer + Print enabled</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Viewer + Print + Download</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Images</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Display in viewer</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Display + Print</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Display + Print + Download</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Word Docs</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Convert to PDF for viewing</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Convert + Print</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Original file download + Print</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Excel</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Convert to HTML/PDF for viewing</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Convert + Print</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Original file download + Print</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Text Files</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Display text in viewer</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Display + Print</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Display + Print + Download</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Other Formats</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Preview if available</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Preview + Print</td>
                  <td className="px-4 py-3 text-sm text-gray-600">Download (even if no preview)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Print vs Download Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Info className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">About Print vs Download Permissions</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Note:</strong> While there's minimal practical difference between Print and Download permissions 
              (users can screenshot, photograph, or "Print to PDF"), we maintain both permission levels for:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 text-sm text-gray-700">
              <li><strong>Compliance requirements:</strong> Some industries require distinct permission types for audit trails</li>
              <li><strong>User intent tracking:</strong> Different actions indicate different use cases for analytics</li>
              <li><strong>Workflow differentiation:</strong> Print for physical copies, Download for digital files</li>
            </ul>
            <p className="text-sm text-gray-700 mt-3">
              <strong>Key Protection:</strong> All files are watermarked with user email and timestamp, which is the primary 
              security mechanism regardless of permission level.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataroomFileSettingsPage;

